// The desktop bridge reference (window.moodsDesktop, exposed by apps/desktop's preload) without any app imports, so
// low-level modules (store/auth, lib/api) can use it without import cycles. Everything else goes through
// lib/desktop.ts. In a browser `desktop` is null and every helper here is a no-op.
import type { MoodsDesktopBridge } from '@moods/shared';

export const desktop: MoodsDesktopBridge | null =
  typeof window !== 'undefined' ? (window.moodsDesktop ?? null) : null;

/** Refresh-token persistence for lib/api's refresh flow (OS-encrypted storage in the desktop shell). */
export const desktopTokenHooks: {
  getStoredRefreshToken?: () => Promise<string | null>;
  storeRefreshToken?: (token: string | null) => Promise<void>;
} = desktop
  ? {
      getStoredRefreshToken: () => desktop.auth.getRefreshToken().catch(() => null),
      storeRefreshToken: token => desktop.auth.setRefreshToken(token).catch(() => undefined),
    }
  : {};

/** Remembers / forgets the desktop refresh token (no-op on the web). */
export function storeDesktopRefreshToken(token: string | null | undefined): void {
  if (!desktop || token === undefined) return;
  void desktop.auth.setRefreshToken(token).catch(() => undefined);
}

/**
 * The desktop's "known device" token (new-device verification): the API returns it in the body for desktop
 * platforms, the shell keeps it encrypted across sign-outs, logins send it back. Browsers use the moods_dt cookie.
 */
export async function desktopDeviceToken(): Promise<string | undefined> {
  if (!desktop?.auth.getDeviceToken) return undefined;
  return (await desktop.auth.getDeviceToken().catch(() => null)) ?? undefined;
}

export function storeDesktopDeviceToken(token: string | undefined): void {
  if (!desktop?.auth.setDeviceToken || !token) return;
  void desktop.auth.setDeviceToken(token).catch(() => undefined);
}
